// New from Template (LibraryModel+Templates on the Mac): the built-ins that suit where the new document goes (a Scene or
// a Chapter in the manuscript; a Character, Place or Research note elsewhere), the writer's own (the top-level items of
// the Templates folder in Research & Notes), and Save Selection as Template (a copy into that folder, made on first
// use). A new document goes after the selection, or inside it when it's a folder (the top level with nothing chosen); in a
// journal's posts a template makes a post, where New Post puts one, as a draft.
// They fold under one item, New from Template, opened in place (the Mac's New from Template submenu holds the same).
import { manuscriptKind, type ArrangementInfo, type FragmentKind, type MetadataValue, type Project, type TreeNode } from '@writing-suite/core';
import { builtInTemplatesFor, JOURNAL_STATUS, newDocumentPlacement, PlanEditor, type PlanPlacement } from '@writing-suite/core/plan';
import { newPlacement } from '../structure.ts';
import { announce } from '../../shell/context.ts';
import type { LiveProject } from '../../store/project.ts';
import { planEdit } from '../../store/plan.ts';
import { hasChildren } from '../helpers.ts';
import type { MenuEntry } from '../Menu.tsx';

export interface TemplateContext {
  project: Project;
  live: LiveProject | null;
  arrangement: ArrangementInfo;
  node: TreeNode | null;
  canStructure: boolean;
  /** Opens the new document (its node, in this arrangement) in Write. */
  opened(nodeId: string): void;
}

/** The writer's own templates (read, not written). */
export function customTemplates(live: LiveProject | null): { nodeID: string; title: string }[] {
  if (!live) return [];
  try {
    return new PlanEditor(live.documents(), { device: 'web', author: '' }).customTemplates();
  } catch {
    return [];
  }
}

/** New from Template: a folded section of the templates, then Save Selection as Template. */
export function templateEntries(c: TemplateContext): MenuEntry[] {
  const disabled = !c.canStructure || !c.live;
  const inManuscript = c.arrangement.role === 'manuscript' || c.arrangement.role === 'alternate';
  const journal = c.project.type === 'journal';
  const makesPost = journal && c.arrangement.role === 'manuscript';
  let placement: PlanPlacement = newDocumentPlacement(c.node ? { id: c.node.id, isFolder: hasChildren(c.node) } : null);
  if (makesPost) {
    const p = newPlacement(c.project.tree(c.arrangement.id), c.node?.id ?? null, { folder: false, post: true });
    placement = 'inside' in p ? { firstIn: p.inside } : { firstIn: null };
  }
  const top: Record<string, MetadataValue> = makesPost ? { status: { type: 'text', value: JOURNAL_STATUS.draft } } : {};
  const kind: FragmentKind = inManuscript ? manuscriptKind(c.project.type) : 'note';
  const make = (fn: (e: PlanEditor) => string, name: string) => {
    if (!c.live) return;
    const nodeId = planEdit(c.live, fn);
    announce(`New ${name}`);
    c.opened(nodeId);
  };
  const own = customTemplates(c.live);
  const entries: MenuEntry[] = [
    ...builtInTemplatesFor(c.arrangement.role, journal).map((t): MenuEntry => ({
      label: t.name,
      disabled,
      onSelect: () => make((e) => e.newDocumentFromTemplate(t, c.arrangement.id, placement, kind, top), t.name),
    })),
  ];
  if (own.length) {
    entries.push({ kind: 'heading', label: 'Your Templates' });
    for (const t of own) {
      entries.push({
        label: t.title,
        disabled,
        onSelect: () =>
          make((e) => {
            const folder = e.templatesFolder();
            if (!folder) throw new Error('The Templates folder has gone.');
            return e.copyDocument(t.nodeID, folder.arrangementID, c.arrangement.id, placement, inManuscript ? kind : undefined, top);
          }, t.title),
      });
    }
  }
  entries.push({ kind: 'separator' }, {
    label: 'Save Selection as Template',
    disabled: disabled || !c.node,
    onSelect: () => {
      if (!c.live || !c.node) return;
      const node = c.node;
      planEdit(c.live, (e) => e.saveAsTemplate(node.id, c.arrangement.id));
      announce(`“${node.title || 'Untitled'}” saved as a template`);
    },
  });
  return [{ kind: 'section', label: 'New from Template', entries }];
}
